import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Link,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Text,
} from "@chakra-ui/react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { UserInvitationByToken } from "@tether-web-portal/api/auth/types";
import { useDiscoverSsoProviders } from "@tether-web-portal/api/sso/hooks";
import SsoLoginButton from "./SsoLoginButton";

const schema = yup.object().shape({
  username: yup.string().email("Email must be a valid email").required("Email is required"),
  password: yup.string().min(4, "Password must be at least 4 characters").required(),
});

export type LoginFormInputs = {
  username: string;
  password: string;
};

interface LoginFormProps {
  onSubmit: (credentials: LoginFormInputs) => void;
  isProcessing: boolean;
  invitation?: UserInvitationByToken;
}

export default function LoginForm({ onSubmit, isProcessing, invitation }: LoginFormProps) {
  const urlParams = new URLSearchParams(window.location.search);
  const emailFromUrl = urlParams.get("email");

  const prNumber = import.meta.env.VITE_PR_NUMBER;
  const githubRepo = import.meta.env.VITE_GITHUB_REPOSITORY;
  let prLink = null;
  if (prNumber && githubRepo) {
    prLink = `https://github.com/${githubRepo}/pull/${prNumber}`;
  }

  const [ssoEmail, setSsoEmail] = useState<string>(
    emailFromUrl || invitation?.email || ""
  );
  const [ssoError, setSsoError] = useState<string | null>(null);
  const ssoLookupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      username: emailFromUrl ?? "",
    },
  });

  // useQuery is internally enabled when the email looks like a complete address
  const { providers: ssoProviders } = useDiscoverSsoProviders(ssoEmail);

  // Debounce updating the email used by the discovery query so we don't fire
  // on every keystroke
  const scheduleDiscovery = (value: string) => {
    if (ssoLookupTimer.current) clearTimeout(ssoLookupTimer.current);
    ssoLookupTimer.current = setTimeout(() => setSsoEmail(value), 300);
  };

  const handleEmailChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue("username", value);
    if (errors?.username?.message) {
      trigger("username");
    }
    if (ssoError) {
      setSsoError(null);
    }
    scheduleDiscovery(value);
  };

  // Also look up SSO providers on blur for cases where autofill bypasses onChange.
  // We override react-hook-form's registered onBlur here, so re-run field
  // validation manually to preserve the form's mode: "onBlur" behaviour.
  const handleEmailBlur = () => {
    trigger("username");
    const currentEmail = watch("username");
    if (currentEmail) {
      setSsoEmail(currentEmail);
    }
  };

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (ssoLookupTimer.current) {
        clearTimeout(ssoLookupTimer.current);
      }
    };
  }, []);

  const handleSsoError = (error: Error) => {
    setSsoError(error.message);
  };

  return (
    <>
      {prLink && (
        <Alert
          status="info"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          mb={4}
          mx={4}
          mt={2}
          borderRadius="md"
        >
          <AlertIcon boxSize="20px" mr={0} />
          <AlertTitle mt={2} mb={1} fontSize="md">
            This is a Preview Build
          </AlertTitle>
          <AlertDescription maxWidth="sm" fontSize="sm">
            You are viewing a preview for Pull Request #{prNumber}.
            <br />
            <Link href={prLink} isExternal color="blue.500" fontWeight="semibold">
              View PR on GitHub
            </Link>
          </AlertDescription>
        </Alert>
      )}
      {ssoError && (
        <Alert status="error" mb={4} mx={4} borderRadius="md">
          <AlertIcon />
          {ssoError}
        </Alert>
      )}
      <form style={{ display: "flex", flexDirection: "column" }}>
        <FormControl isInvalid={!!errors?.username?.message} p="4" isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            placeholder="Email"
            defaultValue={invitation ? invitation.email : ""}
            {...register("username")}
            onChange={handleEmailChanged}
            onBlur={handleEmailBlur}
          />
          <FormErrorMessage>{errors?.username?.message}</FormErrorMessage>
        </FormControl>
        {ssoProviders.length > 0 && (
          <Box px="4" pb="2">
            {ssoProviders.map((provider) => (
              <SsoLoginButton
                key={`${provider.providerType}-${provider.organisationId}`}
                providerType={provider.providerType}
                organisationId={provider.organisationId}
                onError={handleSsoError}
                onAttemptStart={() => setSsoError(null)}
              />
            ))}
            <Box display="flex" alignItems="center" my={4}>
              <Divider />
              <Text px={3} color="gray.400" fontSize="sm" whiteSpace="nowrap">
                or
              </Text>
              <Divider />
            </Box>
          </Box>
        )}
        <FormControl isInvalid={!!errors?.password?.message} px="4" pb="4" isRequired>
          <FormLabel>Password</FormLabel>
          <Input placeholder="Password" type={"password"} {...register("password")} />
          <FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
        </FormControl>
        <Button
          onClick={handleSubmit(onSubmit)}
          p="4"
          mx="4"
          mt="6"
          alignSelf="stretch"
          colorScheme="blue"
          variant="solid"
          type="submit"
          disabled={isProcessing || !!errors.username || !!errors.password}
        >
          {Boolean(isProcessing) ? "Logging in..." : "Login"}
        </Button>
        <Box w="100%" justifyContent="center" display="flex">
          <Link
            href={`/auth/request-password${window.location.search}`}
            color="gray.300"
            fontSize="md"
            textAlign="center"
            display="inline-block"
            mt={5}
          >
            Forgotten password?
          </Link>
        </Box>
      </form>
    </>
  );
}
